import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiMapPin, FiPhone, FiMail, FiX, FiSend } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import logo from "../assets/uploads/logos/1780688835226-584334.jpg";
import "./Footer.css";

/* ══════════════════════════════════════════════════════════
   PHP → REACT ROUTE MAPPER
   Mirrors the exact same logic used in Navbar.jsx so that
   any link value stored in the navbar DB table resolves to
   the correct React Router path regardless of whether it
   was saved as a .php filename, a bare slug, or a full path.
   ══════════════════════════════════════════════════════════ */
function toPath(link) {
  if (!link) return "/";

  /* Known legacy PHP filenames → React routes */
  const phpMap = {
    "index.php":           "/",
    "services.php":        "/services",
    "products.php":        "/products",
    "about-us.php":        "/about-us",
    "portfolio.php":       "/portfolio",
    "contact.php":         "/contact",
    "shop.php":            "/shop",
    "Cars-We-Service.php": "/cars-we-service",
  };

  if (phpMap[link]) return phpMap[link];

  /* Any remaining .php filename not in the map:
     strip the extension and lowercase it */
  if (link.endsWith(".php")) {
    return "/" + link.replace(/\.php$/i, "").toLowerCase();
  }

  /* Already a proper path */
  if (link.startsWith("/")) return link;

  /* Bare slug — prepend slash */
  return `/${link}`;
}

/* ══════════════════════════════════════════════════════════
   SOCIAL ICON RESOLVER
   Maps icon_class strings stored in social_links DB to SVGs.
   Add more keys as new platforms are added in admin.
   ══════════════════════════════════════════════════════════ */
function SocialIcon({ iconClass, size = 15 }) {
  const cls = (iconClass || "").toLowerCase();

  const icons = {
    facebook:  "M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z",
    twitter:   "M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z",
    instagram: null,
    linkedin:  "M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z M4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
    youtube:   "M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z",
    whatsapp:  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z M12 0C5.373 0 0 5.373 0 12c0 2.126.554 4.12 1.527 5.849L0 24l6.341-1.498A11.95 11.95 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z",
    tiktok:    "M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.28 6.28 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.02-.06z",
  };

  const matchKey = Object.keys(icons).find((k) => cls.includes(k));

  if (cls.includes("instagram")) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
      </svg>
    );
  }

  if (!matchKey || !icons[matchKey]) {
    return (
      <span style={{ fontSize: size * 0.7, fontWeight: 700, lineHeight: 1 }}>
        {(iconClass || "?").slice(0, 2).toUpperCase()}
      </span>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d={icons[matchKey]} />
    </svg>
  );
}

/* Fallback WhatsApp number if not found in social_links DB */
const WHATSAPP_NUMBER = "27829362678";

/* ════════════════════════════════════════════════════════════ */
export default function Footer() {

  /* ── API state ── */
  const [contactInfo,  setContactInfo]  = useState(null);
  const [aboutTagline, setAboutTagline] = useState("");
  const [navItems,     setNavItems]     = useState([]);
  const [socialLinks,  setSocialLinks]  = useState([]);

  /* ── Local state ── */
  const [email,    setEmail]    = useState("");
  const [subSent,  setSubSent]  = useState(false);
  const [subError, setSubError] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatForm, setChatForm] = useState({ fullName: "", email: "", phone: "", message: "" });
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const year = new Date().getFullYear();

  /* ── Fetch all data in parallel ── */
  useEffect(() => {
    /* 1. Company contact */
    fetch(`${process.env.REACT_APP_API_URL}/api/company-contact`)
      .then((r) => r.json())
      .then((d) => { if (d?.id) setContactInfo(d); })
      .catch(() => {});

    fetch(`${process.env.REACT_APP_API_URL}/api/about-us`)
      .then((r) => r.json())
      .then((d) => { if (d?.meta_description) setAboutTagline(d.meta_description); })
      .catch(() => {});

    fetch(`${process.env.REACT_APP_API_URL}/api/navbar`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setNavItems(d); })
      .catch(() => {});

    fetch(`${process.env.REACT_APP_API_URL}/api/social-links?placement=footer`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setSocialLinks(d); })
      .catch(() => {});
  }, []);

  /* ══════════════════════════════════════
     DERIVED DATA
     ══════════════════════════════════════ */

  /* Contact fields */
  const phone       = contactInfo?.phone_number        || null;
  const emailAddr   = contactInfo?.email_address       || null;
  const address     = contactInfo?.physical_address    || null;
  const addressLink = contactInfo?.physical_address_link || "#";

  const addressLines = address
    ? address.split(/\n|,/).map((l) => l.trim()).filter(Boolean)
    : [];

  const phoneLines = phone
    ? phone.split(/[\n/]/).map((p) => p.trim()).filter(Boolean)
    : [];

  const emailLines = emailAddr
    ? emailAddr.split(/[\n,]/).map((e) => e.trim()).filter(Boolean)
    : [];

  /* ── Quick Links: all active PARENT navbar items (parent_id === null) ── */
  const quickLinks = navItems.filter((n) => n.parent_id === null && n.active !== 0);

  /* ── Our Services: top 7 children whose parent label contains "service" ──
     Falls back to first parent's children if no "Services" parent is found.  */
  const serviceParent = navItems.find(
    (n) => n.parent_id === null && n.label && n.label.toLowerCase().includes("service")
  );
  const serviceLinks = serviceParent?.children
    ? serviceParent.children.filter((c) => c.active !== 0).slice(0, 7)
    : [];

  /* ── WhatsApp number: from social_links or fallback constant ── */
  const waSocial = socialLinks.find((s) =>
    (s.platform || "").toLowerCase().includes("whatsapp")
  );
  const waNumber = waSocial
    ? waSocial.url.replace(/[^0-9]/g, "")
    : WHATSAPP_NUMBER;

  /* ══════════════════════════════════════
     HANDLERS
     ══════════════════════════════════════ */

  /* Newsletter subscribe — POST to /api/newsletter/subscribe */
  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubError("");
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL}/api/newsletter/subscribe`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email }),
      });
      const data = await r.json();
      if (!r.ok) {
        /* If already subscribed (409), still show success to the user */
        if (r.status === 409) {
          setSubSent(true);
          setEmail("");
          setTimeout(() => setSubSent(false), 3000);
        } else {
          setSubError(data.message || "Subscription failed.");
        }
        return;
      }
      setSubSent(true);
      setEmail("");
      setTimeout(() => { setSubSent(false); setSubError(""); }, 3000);
    } catch {
      setSubError("Network error. Please try again.");
    }
  };

  /* WhatsApp chat form */
  const handleChatChange = (e) =>
    setChatForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleChatSend = (e) => {
    e.preventDefault();
    setSending(true);
    const text = encodeURIComponent(
      `*New Enquiry from Website*\n\n` +
      `*Full Name:* ${chatForm.fullName}\n` +
      `*Email:* ${chatForm.email}\n` +
      `*Phone:* ${chatForm.phone}\n\n` +
      `*Message:*\n${chatForm.message}`
    );
    window.open(`https://wa.me/${waNumber}?text=${text}`, "_blank", "noopener,noreferrer");
    setSending(false);
    setSent(true);
    setTimeout(() => {
      setChatForm({ fullName: "", email: "", phone: "", message: "" });
      setSent(false);
      setChatOpen(false);
    }, 1200);
  };

  /* ══════════════════════════════════════
     RENDER
     ══════════════════════════════════════ */
  return (
    <>
      <footer className="footer">

        {/* ══════ TOP CONTACT BAR ══════ */}
        <div className="footer__topbar">
          <div className="container footer__topbar-inner">

            {/* Logo — static asset */}
            <Link to="/" className="footer__topbar-logo">
              <img src={logo} alt="Noel Automotive Repairs" />
            </Link>

            {/* Address — from company_contact.physical_address */}
            {addressLines.length > 0 && (
              <a
                href={addressLink !== "#" ? addressLink : undefined}
                target={addressLink !== "#" ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="footer__topbar-contact"
              >
                <div className="footer__topbar-icon"><FiMapPin size={20} /></div>
                <div>
                  <span className="footer__topbar-label">Our Address</span>
                  <span className="footer__topbar-value">{addressLines.join(", ")}</span>
                </div>
              </a>
            )}

            {/* Phone — from company_contact.phone_number */}
            {phoneLines.length > 0 && (
              <a
                href={`tel:${phoneLines[0].replace(/\s/g, "")}`}
                className="footer__topbar-contact"
              >
                <div className="footer__topbar-icon"><FiPhone size={20} /></div>
                <div>
                  <span className="footer__topbar-label">Call Us</span>
                  {phoneLines.map((p, i) => (
                    <span key={i} className="footer__topbar-value">{p}</span>
                  ))}
                </div>
              </a>
            )}

            {/* Email — from company_contact.email_address */}
            {emailLines.length > 0 && (
              <a
                href={`mailto:${emailLines[0]}`}
                className="footer__topbar-contact"
              >
                <div className="footer__topbar-icon"><FiMail size={20} /></div>
                <div>
                  <span className="footer__topbar-label">Email Us</span>
                  {emailLines.map((em, i) => (
                    <span key={i} className="footer__topbar-value">{em}</span>
                  ))}
                </div>
              </a>
            )}

          </div>
        </div>

        {/* ══════ MAIN BODY ══════ */}
        <div className="footer__body">
          <div className="container footer__grid">

            {/* ── Col 1: Brand ──
                Tagline = about_us.meta_description
                Social icons = social_links table (footer placement) */}
            <div className="footer__brand">
              <p className="footer__tagline">
                {aboutTagline ||
                  "A proudly South African formwork and scaffolding operation that is 100% black owned and managed."}
              </p>

              <Link to="/contact" className="footer__build-btn">
                LET US FIX YOUR CAR NOW
                <span className="footer__build-arrow"><FiArrowRight size={16} /></span>
              </Link>

              {/* Social Icons — from social_links (show_in_footer = 1) */}
              {socialLinks.length > 0 && (
                <div className="footer__socials">
                  {socialLinks.map((social) => {
                    const isWa = (social.platform || "").toLowerCase().includes("whatsapp");
                    return (
                      <a
                        key={social.id}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.platform}
                        className={isWa ? "footer__socials-whatsapp" : ""}
                        title={social.platform}
                      >
                        <SocialIcon iconClass={social.icon_class} size={15} />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Col 2: Quick Links ──
                All active PARENT items from navbar table (parent_id IS NULL) */}
            <div>
              <h4 className="footer__col-heading">Quick Links</h4>
              {quickLinks.length > 0 ? (
                <ul className="footer__list">
                  {quickLinks.map((item) => (
                    <li key={item.id}>
                      <Link to={toPath(item.link)}>
                        <span className="footer__list-arrow" aria-hidden="true" />
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="footer__loading">Loading…</p>
              )}
            </div>

            {/* ── Col 3: Our Services ──
                Top 7 active children of the navbar item whose label
                contains "service" (case-insensitive) */}
            <div>
              <h4 className="footer__col-heading">Our Services</h4>
              {serviceLinks.length > 0 ? (
                <ul className="footer__list">
                  {serviceLinks.map((item) => (
                    <li key={item.id}>
                      <Link to={toPath(item.link)}>
                        <span className="footer__list-arrow" aria-hidden="true" />
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : navItems.length > 0 ? (
                /* Shown once navbar has loaded but no Services parent found */
                <p className="footer__loading">No services listed.</p>
              ) : (
                <p className="footer__loading">Loading…</p>
              )}
            </div>

            {/* ── Col 4: Newsletter ──
                Saves to newsletter_subscribers via /api/newsletter/subscribe */}
            <div>
              <h4 className="footer__col-heading">Subscribe News</h4>
              <p className="footer__newsletter-text">
                Get project updates and industry insights — no spam, just value.
              </p>
              <form className="footer__newsletter-form" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="footer__newsletter-input"
                />
                <button
                  type="submit"
                  className="footer__newsletter-btn"
                  aria-label="Subscribe"
                >
                  {subSent ? "✓" : <FiArrowRight size={18} />}
                </button>
              </form>

              {/* Inline error if subscription fails */}
              {subError && (
                <p style={{ fontSize: 12, color: "#e84118", marginBottom: 12 }}>
                  {subError}
                </p>
              )}
            </div>

          </div>
        </div>

        {/* ══════ BOTTOM BAR ══════ */}
        <div className="footer__bottom">
          <div className="container footer__bottom-inner">
            <p>© {year} Noel Automotive Repairs. All rights reserved.</p>
            <div className="footer__bottom-links">
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Use</a>
              <a href="#">Site Map</a>
            </div>
          </div>
        </div>

      </footer>

      {/* ══════ FLOATING WHATSAPP CHAT WIDGET ══════ */}
      <div className={`wa-chat-panel${chatOpen ? " wa-chat-panel--open" : ""}`}>
        <div className="wa-chat-header">
          <div className="wa-chat-header-left">
            <div className="wa-chat-avatar"><FaWhatsapp size={22} /></div>
            <div>
              <div className="wa-chat-name">Noel Automotive Repairs</div>
              <div className="wa-chat-status">
                <span className="wa-chat-dot" />
                Typically replies instantly
              </div>
            </div>
          </div>
          <button
            className="wa-chat-close"
            onClick={() => setChatOpen(false)}
            aria-label="Close chat"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="wa-chat-body">
          <div className="wa-chat-bubble">
            <p>👋 Hi there! Fill in your details below and we'll connect with you on WhatsApp right away.</p>
          </div>
          <form className="wa-chat-form" onSubmit={handleChatSend}>
            <div className="wa-chat-field">
              <label>Full Name *</label>
              <input
                type="text"
                name="fullName"
                placeholder="e.g. John Dlamini"
                value={chatForm.fullName}
                onChange={handleChatChange}
                required
              />
            </div>
            <div className="wa-chat-field">
              <label>Email Address *</label>
              <input
                type="email"
                name="email"
                placeholder="e.g. john@example.com"
                value={chatForm.email}
                onChange={handleChatChange}
                required
              />
            </div>
            <div className="wa-chat-field">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                placeholder="e.g. +27 82 936 2678"
                value={chatForm.phone}
                onChange={handleChatChange}
                required
              />
            </div>
            <div className="wa-chat-field">
              <label>Message *</label>
              <textarea
                name="message"
                placeholder="What would you like to enquire about?"
                value={chatForm.message}
                onChange={handleChatChange}
                required
                rows={3}
              />
            </div>
            <button
              type="submit"
              className={`wa-chat-send${sent ? " wa-chat-send--sent" : ""}`}
              disabled={sending || sent}
            >
              {sent ? "Opening WhatsApp…" : (
                <>
                  <FaWhatsapp size={16} />
                  {sending ? "Sending…" : "Send via WhatsApp"}
                  {!sending && <FiSend size={14} />}
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* FAB */}
      <button
        className={`wa-fab${chatOpen ? " wa-fab--active" : ""}`}
        onClick={() => setChatOpen((prev) => !prev)}
        aria-label="Chat on WhatsApp"
      >
        {chatOpen ? <FiX size={26} /> : <FaWhatsapp size={28} />}
        {!chatOpen && <span className="wa-fab-pulse" />}
      </button>
    </>
  );
}